import { map } from "lodash-es";
import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import {
  DrawerContent,
  DrawerFooter,
  DrawerTopBar,
  DrawerTopBarActionButton,
  SwipeableDrawer,
} from "@/components/Drawer";
import type { Airline } from "@/features/flights/types/types";
import { useToggleSelectAll } from "@/hooks/useToggleSelectAll";
import { FlightFilterTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";

import { AirlinesFilter } from "./flight-filters/AirlinesFilter";

type AirlinesFilterContentsProps = {
  allAirlines?: Airline[];
  defaultFilter?: string[];
  value?: string[];
  onSubmit: (newValue: string[]) => void;
  onCloseClick: () => void;
};

export const AirlinesFilterContents = ({
  allAirlines = [],
  defaultFilter = [],
  value = [],
  onSubmit,
  onCloseClick,
}: AirlinesFilterContentsProps) => {
  const intl = useIntl();

  const [localFilter, setLocalFilter] = useState(value);
  const allAirlinesCodes = map(allAirlines, "code");

  const {
    manuallySelectedAll,
    setManuallySelectedAll,
    areAllSelected,
    toggle,
  } = useToggleSelectAll<string>({
    selected: localFilter,
    all: allAirlinesCodes,
  });

  const isInDefaultState = areAllSelected && !manuallySelectedAll;

  return (
    <>
      <DrawerTopBar
        onCloseClick={onCloseClick}
        title={intl.formatMessage({
          defaultMessage: "Airlines",
          description: "Filter section label for filtering flights by airline",
        })}
        actionElement={
          allAirlines.length > 1 ? (
            <DrawerTopBarActionButton
              onClick={() => {
                if (isInDefaultState) {
                  setLocalFilter(allAirlinesCodes);
                  setManuallySelectedAll(true);
                } else {
                  setLocalFilter(defaultFilter);
                  setManuallySelectedAll(false);
                }

                rudderAnalytics.track(
                  FlightFilterTrackingEvents.USER_CHANGED_FLIGHTS_FILTER,
                  {
                    source: "separate",
                    filter_name: "airlines",
                    is_clear_action: !isInDefaultState,
                    is_select_all_action: isInDefaultState,
                    prev_airlines: localFilter,
                    curr_airlines: isInDefaultState
                      ? allAirlinesCodes
                      : defaultFilter,
                  },
                );
              }}
            >
              {isInDefaultState
                ? intl.formatMessage({
                    defaultMessage: "Select All",
                    description: "Button to select all items in a list at once",
                  })
                : intl.formatMessage({
                    defaultMessage: "Clear filter",
                    description: "Button to remove a single active filter",
                  })}
            </DrawerTopBarActionButton>
          ) : null
        }
      />

      <DrawerContent>
        <AirlinesFilter
          airlines={allAirlines}
          selectedAirlines={localFilter}
          userManuallySelectedAllAirlines={manuallySelectedAll}
          onSingleAirlineChange={(e, checked, airline) => {
            e.stopPropagation();
            const newSelected = toggle(localFilter, airline.code, checked);
            rudderAnalytics.track(
              FlightFilterTrackingEvents.USER_CHANGED_FLIGHTS_FILTER,
              {
                source: "separate",
                filter_name: "airlines",
                is_clear_action: false,
                is_select_all_action: false,
                prev_airlines: localFilter,
                curr_airlines: newSelected,
              },
            );
            setLocalFilter(newSelected);
          }}
        />
      </DrawerContent>

      <DrawerFooter>
        <Button
          fullWidth
          onClick={() => onSubmit(localFilter)}
          variant="contained"
          size="large"
        >
          {intl.formatMessage({
            defaultMessage: "Apply",
            description: "Button to apply selected filters or changes",
          })}
        </Button>
      </DrawerFooter>
    </>
  );
};

type Props = AirlinesFilterContentsProps & {
  open: boolean;
};

export const AirlinesFilterDrawer = ({
  open,
  allAirlines = [],
  defaultFilter = [],
  value = [],
  onSubmit,
  onCloseClick,
}: Props) => {
  return (
    <SwipeableDrawer open={open} onClose={onCloseClick}>
      <AirlinesFilterContents
        allAirlines={allAirlines}
        defaultFilter={defaultFilter}
        value={value}
        onSubmit={onSubmit}
        onCloseClick={onCloseClick}
      />
    </SwipeableDrawer>
  );
};
